// CHG-123 REQ-V123-031: 글로벌 채널에서 작업 생성 시 프로젝트 선택 모달
import { useState, useEffect, useMemo } from 'react';
import { X, ArrowLeft, Search, FolderKanban } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchProjects, type ProjectDetail } from '../../services/projectService';
import { useDebounce } from '../../hooks/useDebounce';
import Spinner from '../ui/Spinner';

interface ProjectPickerModalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  onSelect: (project: { id: string; name: string; projectKey: string; projectType: string }) => void;
}

export default function ProjectPickerModal({ open, onClose, onBack, onSelect }: ProjectPickerModalProps) {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 200);

  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['projects', 'forMessageToTask'],
    // CHG-132: 옵셔널 archived 인자 추가로 직접 참조 시 타입 좁혀짐 — 람다로 감싸 호환 유지
    queryFn: () => fetchProjects(),
    enabled: open,
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    if (!data) return [] as ProjectDetail[];
    const q = debounced.trim().toLowerCase();
    if (!q) return data;
    return data.filter((p) =>
      p.name.toLowerCase().includes(q) || p.projectKey.toLowerCase().includes(q),
    );
  }, [data, debounced]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-picker-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="뒤로">
              <ArrowLeft className="size-5" />
            </button>
            <h3 id="project-picker-title" className="text-lg font-semibold text-gray-900">프로젝트 선택</h3>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="닫기">
            <X className="size-5" />
          </button>
        </div>

        {/* 검색 */}
        <div className="relative mb-3 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="프로젝트 이름 또는 키로 검색"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500"
            autoFocus
          />
        </div>

        {/* 결과 리스트 */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading && (
            <div className="flex justify-center py-6">
              <Spinner className="size-6" />
            </div>
          )}

          {isError && !isLoading && (
            <p className="text-sm text-red-500 py-4 text-center">프로젝트 목록을 불러오지 못했습니다.</p>
          )}

          {!isLoading && !isError && filtered.length === 0 && (
            <p className="text-sm text-gray-400 py-6 text-center">
              {data && data.length === 0 ? '참여 중인 프로젝트가 없습니다.' : '검색 결과가 없습니다.'}
            </p>
          )}

          <ul className="space-y-1">
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => onSelect({ id: p.id, name: p.name, projectKey: p.projectKey, projectType: p.projectType })}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-primary-50 text-left transition-colors"
                >
                  <FolderKanban className="size-5 text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900 truncate">{p.name}</span>
                      <span className="text-sm text-gray-400 shrink-0">{p.projectKey}</span>
                    </div>
                    <p className="text-sm text-gray-500">{p.projectType}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
